/**
 * Pure helpers for the match create / edit forms (kept out of the components
 * so they can be unit tested).
 */
import { getUserRoles, isCreatorOf } from "./rbac";

/** Roles the backend treats as match admins (force_create, edits, deletes). */
export const MATCH_ADMIN_ROLES = [
  "super_admin",
  "platform_admin",
  "organization_admin",
  "league_admin",
];

/** Minimum lead time for a new match, in minutes (the hint says 5). */
export const MIN_SCHEDULE_LEAD_MINUTES = 5;

const CONFIG_KEYS = [
  "points_to_win",
  "win_by_margin",
  "max_points",
  "total_games",
  "periods",
  "period_minutes",
  "overtime_minutes",
];

const norm = (v) => (v === undefined || v === "" ? null : v);

/**
 * True when the chosen format differs from the selected sport's own defaults
 * (MT-B26: previously every sport was compared with badminton's 21 / best of 3).
 */
export const isMatchConfigCustomized = (config, defaults) => {
  if (!config || !defaults) return false;
  return CONFIG_KEYS.some((key) => {
    if (!(key in config) && !(key in defaults)) return false;
    return norm(config[key]) !== norm(defaults[key]);
  });
};

/**
 * "Create Anyway" after a schedule conflict: the backend honours force_create
 * only for admins and for the host of the league / tournament the match is in.
 */
export const canForceCreateMatch = (user, { tournament = null, league = null } = {}) => {
  if (!user) return false;
  if (getUserRoles(user).some((r) => MATCH_ADMIN_ROLES.includes(r))) return true;
  return isCreatorOf(user, tournament) || isCreatorOf(user, league);
};

/** datetime-local value (local time, no zone) for `now + minutes`. */
export const localDateTimeInputValue = (now = Date.now(), minutes = MIN_SCHEDULE_LEAD_MINUTES) => {
  const d = new Date(now + minutes * 60 * 1000);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

/**
 * PUT /matches/:id body from the MatchEdit form: only the fields the host
 * changed (MT-B19 — scheduled_at used to be sent every time, so editing notes
 * after the start time failed with "cannot be in the past"). With notesOnly
 * (the match is no longer scheduled) nothing but the notes is sent.
 */
export const buildMatchUpdatePayload = (initial = {}, form = {}, { notesOnly = false } = {}) => {
  const toApi = (f) => ({
    venue: f.venue || "",
    venue_address: f.venue_address || null,
    venue_lat: f.venue_lat || null,
    venue_lng: f.venue_lng || null,
    place_id: f.venue_place_id || null,
    court: f.court || null,
    // The wall time at the venue (datetime-local, no offset): the server reads
    // it in the match's zone (backend utils/eventTime)
    scheduled_at: f.scheduled_at || null,
    notes: f.description || "",
    referee_id: f.referee_id || null,
  });
  const keys = notesOnly ? ["notes"] : undefined;
  const after = toApi(form);
  return diffChangedFields(toApi(initial), after, keys || Object.keys(after));
};

/**
 * Only the fields that changed between the loaded match and the form.
 * Values are compared as strings so "3" and 3 are equal.
 */
export const diffChangedFields = (original = {}, current = {}, keys = Object.keys(current)) => {
  const changed = {};
  keys.forEach((key) => {
    const a = original[key] === undefined || original[key] === null ? "" : original[key];
    const b = current[key] === undefined || current[key] === null ? "" : current[key];
    const same = typeof a === "object" || typeof b === "object"
      ? JSON.stringify(a) === JSON.stringify(b)
      : String(a) === String(b);
    if (!same) changed[key] = current[key];
  });
  return changed;
};
